(() => {
  const ROOT = '[data-vh-pdp-set]';
  const DISCOUNT_RATE = 0.06;
  const routeRoot = () => window.Shopify?.routes?.root || '/';
  const currency = () => window.Shopify?.currency?.active || 'PLN';

  const normalize = (value) => String(value || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const textWithTokens = (text, tokens = {}) => {
    let output = String(text || '');
    Object.entries(tokens).forEach(([token, value]) => {
      output = output.split(token).join(String(value));
    });
    return output;
  };

  const parseI18n = (block) => {
    const node = block.querySelector('[data-vh-bundle-i18n]');
    try {
      const value = JSON.parse(node?.textContent || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (_) {
      return {};
    }
  };

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: currency(), minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency()}`;
    }
  };

  const availableVariants = (product) => Array.isArray(product?.variants) ? product.variants.filter((variant) => variant?.available) : [];
  const optionValue = (variant, index) => String(variant?.[`option${index + 1}`] ?? variant?.options?.[index] ?? '');
  const optionNames = (product, t = {}) => (Array.isArray(product?.options) ? product.options : []).map((option) => typeof option === 'string' ? option : (option?.name || t.variant || ''));
  const uniqueValues = (variants, index) => [...new Set(variants.map((variant) => optionValue(variant, index)).filter(Boolean))];

  const isNightstand = (product) => {
    const handle = String(product?.handle || '').toLowerCase();
    const title = normalize(product?.title);
    return handle.includes('szafka-nocna')
      || ['szafka nocna', 'nachttisch', 'bedside table', 'nocni stolek', 'mesita de noche', 'comodino'].some((needle) => title.includes(needle));
  };

  const isNightstandBundle = (block) => block.dataset.vhNightstandBundle === 'true';
  const productUrl = (product) => product?.handle ? `${routeRoot()}products/${product.handle}` : '#';
  const productImage = (product, variant) => {
    const source = variant?.featured_image || product?.featured_image || product?.images?.[0];
    if (!source) return '';
    return typeof source === 'string' ? source : (source.src || source.url || '');
  };

  const variantSummary = (product, variant, t) => {
    if (!variant) return '';
    const values = optionNames(product, t)
      .map((_, index) => optionValue(variant, index))
      .filter((value) => value && normalize(value) !== 'default title');
    return values.join(' · ');
  };

  const currentProductJson = async () => {
    const response = await fetch(`${location.pathname.replace(/\/$/, '')}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load product data.');
    return response.json();
  };

  const cartJson = async () => {
    const response = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load cart data.');
    return response.json();
  };

  const manualComplementaryHandles = (block) => {
    try {
      const node = block.querySelector('[data-vh-complementary-handles]');
      const handles = JSON.parse(node?.textContent || '[]');
      return Array.isArray(handles) ? handles.filter(Boolean).slice(0, 3) : [];
    } catch (_) {
      return [];
    }
  };

  const fetchProductByHandle = async (handle) => {
    const response = await fetch(`${routeRoot()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load complementary product.');
    return response.json();
  };

  const fetchRecommendations = async (block, productId) => {
    const handles = manualComplementaryHandles(block);

    if (handles.length) {
      const manualProducts = (await Promise.all(
        handles.map((handle) => fetchProductByHandle(handle).catch(() => null))
      )).filter((product) => product && Number(product.id) !== Number(productId) && availableVariants(product).length);

      if (manualProducts.length) return manualProducts.slice(0, 3);
    }

    for (const intent of ['complementary', 'related']) {
      try {
        const response = await fetch(`${routeRoot()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=3&intent=${intent}`, { headers: { Accept: 'application/json' } });
        if (!response.ok) continue;
        const data = await response.json();
        const products = Array.isArray(data?.products)
          ? data.products.filter((product) => Number(product?.id) !== Number(productId) && availableVariants(product).length)
          : [];
        if (products.length) return products.slice(0, 3);
      } catch (_) {}
    }

    return [];
  };

  const currentMainVariant = (block) => {
    const state = block._vhBundle;
    if (!state?.mainProduct) return null;
    return state.mainProduct.variants?.find((variant) => Number(variant.id) === Number(state.mainVariantId))
      || state.mainProduct.variants?.find((variant) => variant.available)
      || state.mainProduct.variants?.[0]
      || null;
  };

  const saveText = (block, saving, qty) => {
    const t = block._vhBundle?.i18n || {};
    return textWithTokens(t.saveText, { '__SAVE__': saving, '__QTY__': qty });
  };

  const recalc = (block) => {
    const main = currentMainVariant(block);
    if (!main?.price || !block._vhBundle?.rows) return;
    const summary = block.closest('.vh-pdp-bundle-offer')?.querySelector('[data-vh-set-summary-saving]');
    if (summary) summary.textContent = textWithTokens(block._vhBundle.i18n.pairSaving, {
      '__SAVE__': money(Math.round(Number(main.price) * 2 * DISCOUNT_RATE)),
    });
    const savings = [];
    block._vhBundle.rows.forEach((state) => {
      if (!state.variant?.price) return;
      const saving = Math.round((Number(main.price) + Number(state.variant.price)) * DISCOUNT_RATE);
      state.saving.textContent = saveText(block, money(saving), 2);
      savings.push(saving);
    });
    const top = block.querySelector('[data-vh-bundle-saving]');
    if (top) top.textContent = savings.length ? saveText(block, money(Math.min(...savings)), 2) : saveText(block, '−6%', 2);
  };

  const updateCartState = (block, cart) => {
    const node = block.querySelector('[data-vh-bundle-cart-state]');
    if (!node || !cart) return;
    const count = Number(cart.item_count || 0);
    node.classList.toggle('is-visible', count > 0);
    if (count >= 2) {
      node.innerHTML = `<strong>${escapeHtml(block._vhBundle?.i18n?.bundleLabel || '')}</strong> ${escapeHtml(saveText(block, '−6%', count))}`;
    } else if (count === 1) {
      node.textContent = saveText(block, '−6%', 2);
    } else {
      node.textContent = '';
    }
  };

  const setVariant = (block, state, variant) => {
    if (!variant) return;
    state.variant = variant;
    state.price.textContent = money(variant.price);
    const image = productImage(state.product, variant);
    if (image && state.image) state.image.src = image;
    if (state.matchSummary) state.matchSummary.textContent = variantSummary(state.product, variant, block._vhBundle?.i18n || {});
    if (state.addButton) state.addButton.disabled = Boolean(block._vhBundle?.pending) || !variant.available;
    recalc(block);
  };

  const makeSelect = (labelText, values) => {
    const label = document.createElement('label');
    label.className = 'vh-bundle-refine__field';
    const caption = document.createElement('span');
    caption.className = 'vh-bundle-refine__label';
    caption.textContent = labelText;
    const select = document.createElement('select');
    select.className = 'vh-bundle-refine__select';
    select.setAttribute('aria-label', labelText);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
    if (values.length <= 1) select.disabled = true;
    label.append(caption, select);
    return { label, select };
  };

  const buildNightstandSelectors = (block, state) => {
    const t = block._vhBundle?.i18n || {};
    const names = optionNames(state.product, t);
    const normalized = names.map(normalize);
    const used = new Set();
    const findIndex = (needles, fallback) => {
      let index = normalized.findIndex((name, i) => !used.has(i) && needles.some((needle) => name.includes(needle)));
      if (index < 0 && fallback < Math.max(names.length, 3) && !used.has(fallback)) index = fallback;
      if (index >= 0) used.add(index);
      return index;
    };

    const indices = [
      findIndex(['kolor', 'color', 'farbe', 'barva', 'colore'], 0),
      findIndex(['wersja', 'version', 'verze', 'versione'], 1),
      findIndex(['szerokosc', 'width', 'breite', 'sirka', 'anchura', 'larghezza'], 2),
    ];
    const configs = indices
      .map((index) => [names[index] || t.variant || '', index])
      .filter(([, index]) => index >= 0 && uniqueValues(state.variants, index).length);

    const grid = document.createElement('div');
    grid.className = 'vh-bundle-refine__options vh-bundle-refine__options--nightstand';
    state.optionSelects = new Array(Math.max(names.length, 3)).fill(null);

    configs.forEach(([labelText, index]) => {
      const control = makeSelect(labelText, uniqueValues(state.variants, index));
      state.optionSelects[index] = control.select;
      grid.appendChild(control.label);
      control.select.addEventListener('change', () => {
        const selected = state.optionSelects.map((select) => select?.value || '');
        let variant = state.variants.find((candidate) => selected.every((value, position) => !value || optionValue(candidate, position) === value));
        variant = variant || state.variants.find((candidate) => optionValue(candidate, index) === control.select.value) || state.variant || state.variants[0];
        state.optionSelects.forEach((select, position) => { if (select) select.value = optionValue(variant, position); });
        setVariant(block, state, variant);
      });
    });
    state.optionSelects.forEach((select, index) => { if (select) select.value = optionValue(state.variant, index); });
    return grid;
  };

  const buildGenericSelector = (block, state) => {
    if (state.variants.length <= 1) return null;
    const t = block._vhBundle?.i18n || {};
    const grid = document.createElement('div');
    grid.className = 'vh-bundle-refine__options';
    const label = document.createElement('label');
    label.className = 'vh-bundle-refine__field';
    const caption = document.createElement('span');
    caption.className = 'vh-bundle-refine__label';
    caption.textContent = t.variant || '';
    const select = document.createElement('select');
    select.className = 'vh-bundle-refine__select';
    select.setAttribute('aria-label', t.variant || '');
    state.variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = variant.public_title || variant.title || t.variant || '';
      select.appendChild(option);
    });
    select.addEventListener('change', () => setVariant(block, state, state.variants.find((variant) => Number(variant.id) === Number(select.value)) || state.variants[0]));
    label.append(caption, select);
    grid.appendChild(label);
    return grid;
  };

  const collapse = (state) => {
    const t = state.block._vhBundle?.i18n || {};
    state.controls.hidden = true;
    state.row.classList.remove('is-expanded');
    if (state.toggle) {
      state.toggle.setAttribute('aria-expanded', 'false');
      state.toggle.textContent = t.changeVariant || t.variant || '';
    }
  };

  const toggleRow = (block, state) => {
    const t = block._vhBundle?.i18n || {};
    const expand = state.toggle.getAttribute('aria-expanded') !== 'true';
    if (!expand) return collapse(state);
    block._vhBundle.rows.forEach((other) => { if (other !== state) collapse(other); });
    state.controls.hidden = false;
    state.row.classList.add('is-expanded');
    state.toggle.setAttribute('aria-expanded', 'true');
    state.toggle.textContent = t.showLess || '';
  };

  const cartSectionIds = () => Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map((node) => node.dataset.sectionId).filter(Boolean);

  const addToCart = async (block, state, button) => {
    const t = block._vhBundle?.i18n || {};
    if (block._vhBundle.pending) return;
    const variant = state.variant;
    if (!variant?.available) return;
    const ids = cartSectionIds();
    const items = [{ id: Number(variant.id), quantity: 1 }];
    const body = { items, sections_url: location.pathname };
    if (ids.length) body.sections = ids.join(',');
    block._vhBundle.pending = true;
    block._vhBundle.rows.forEach((row) => { row.addButton.disabled = true; });
    button.textContent = t.adding || t.addToCart || '';
    try {
      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data?.status) throw new Error(data?.description || data?.message || 'Could not add product.');
      const cart = await cartJson();
      updateCartState(block, cart);
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'vh-pdp-bundle', data: { source: 'vh-pdp-bundle', itemCount: Number(cart.item_count || 0), variantId: String(variant.id), sections: data.sections || {} } },
      }));
      button.classList.add('is-added');
      button.textContent = t.added || t.addToCart || '';
      setTimeout(() => {
        button.classList.remove('is-added');
        block._vhBundle.pending = false;
        block._vhBundle.rows.forEach((row) => { row.addButton.disabled = !row.variant?.available; });
        button.textContent = t.addToSet || t.addToCart || '';
        collapse(state);
      }, 900);
    } catch (error) {
      console.error('[Valleans bundle]', error);
      block._vhBundle.pending = false;
      block._vhBundle.rows.forEach((row) => { row.addButton.disabled = !row.variant?.available; });
      button.textContent = t.addToSet || t.addToCart || '';
      const status = block.querySelector('[data-vh-bundle-status]');
      if (status) status.textContent = error.message;
    }
  };

  const buildRow = (block, product, options = {}) => {
    const t = block._vhBundle?.i18n || {};
    const lockedToMain = options.lockedToMain === true;
    const variants = lockedToMain
      ? (Array.isArray(product?.variants) ? product.variants : [])
      : availableVariants(product);
    if (!variants.length) return null;

    const initialVariant = lockedToMain ? (currentMainVariant(block) || variants[0]) : variants[0];
    const row = document.createElement('div');
    row.className = 'vh-sofa-bundle__product';
    if (lockedToMain) row.classList.add('vh-bundle-refine__product--same');

    const imageLink = document.createElement('a');
    imageLink.className = 'vh-sofa-bundle__image-link';
    imageLink.href = productUrl(product);
    const image = document.createElement('img');
    image.className = 'vh-sofa-bundle__image';
    image.alt = product.title || '';
    image.loading = 'lazy';
    const src = productImage(product, initialVariant);
    if (src) image.src = src;
    imageLink.appendChild(image);

    const info = document.createElement('div');
    info.className = 'vh-sofa-bundle__info';
    const name = document.createElement('a');
    name.className = 'vh-sofa-bundle__name';
    name.href = productUrl(product);
    name.textContent = product.title || t.productTitle || '';
    info.appendChild(name);
    if (lockedToMain) {
      const configuration = document.createElement('span');
      configuration.className = 'vh-bundle-refine__configuration';
      configuration.textContent = t.sameConfiguration || '';
      info.appendChild(configuration);
    }

    let matchSummary = null;
    {
      const matchMeta = document.createElement('div');
      matchMeta.className = 'vh-sofa-bundle__meta';
      matchSummary = document.createElement('span');
      matchSummary.className = 'vh-bundle-refine__row-saving';
      matchMeta.appendChild(matchSummary);
      info.appendChild(matchMeta);
    }

    const meta = document.createElement('div');
    meta.className = 'vh-sofa-bundle__meta';
    const price = document.createElement('span');
    price.className = 'vh-sofa-bundle__price';
    price.textContent = money(initialVariant.price);
    const saving = document.createElement('span');
    saving.className = 'vh-bundle-refine__row-saving';
    meta.append(price, saving);
    info.appendChild(meta);

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'vh-bundle-refine__quick-add';
    addButton.textContent = t.addToSet || t.addToCart || '';
    addButton.setAttribute('aria-label', `${t.addToSet || t.addToCart || ''}: ${product.title || ''}`);

    const state = {
      block, row, product, variants, variant: initialVariant, image, price, saving, toggle: null, addButton,
      controls: null, optionSelects: [], lockedToMain, matchSummary,
    };
    const controls = document.createElement('div');
    controls.className = 'vh-bundle-refine__controls';
    controls.hidden = true;
    state.controls = controls;

    if (!lockedToMain) {
      const variantOptions = isNightstand(product) ? buildNightstandSelectors(block, state) : buildGenericSelector(block, state);
      if (variantOptions) controls.appendChild(variantOptions);
    }

    if (!lockedToMain && controls.children.length) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'vh-bundle-refine__change';
      toggle.textContent = t.changeVariant || t.variant || '';
      toggle.setAttribute('aria-expanded', 'false');
      controls.id = `VHBundleOptions-${block.dataset.productId}-${product.id}`;
      toggle.setAttribute('aria-controls', controls.id);
      state.toggle = toggle;
      toggle.addEventListener('click', () => toggleRow(block, state));
      info.appendChild(toggle);
    }
    addButton.addEventListener('click', () => addToCart(block, state, addButton));
    row.append(imageLink, info, addButton);
    if (!lockedToMain) row.appendChild(controls);
    setVariant(block, state, initialVariant);
    return { row, state };
  };

  const syncLockedRow = (block) => {
    const state = block._vhBundle;
    if (!state) return;
    const variant = currentMainVariant(block);
    const lockedRow = state.rows.find((rowState) => rowState.lockedToMain);
    if (variant && lockedRow) setVariant(block, lockedRow, variant);
    else recalc(block);
    state.rows.forEach(row => {
      row.addButton.disabled = Boolean(state.pending) || !row.variant?.available;
    });
  };

  const initBlock = async (block) => {
    if (block._vhBundle || block.dataset.vhBundleLoading === 'true') return;
    block.dataset.vhBundleLoading = 'true';
    const productId = block.dataset.productId;
    if (!productId) return;
    try {
      const [mainProduct, products, cart] = await Promise.all([
        currentProductJson(), fetchRecommendations(block, productId), cartJson().catch(() => null),
      ]);
      block._vhBundle = {
        mainProduct,
        mainVariantId: Number(block.dataset.selectedVariantId || 0),
        rows: [],
        i18n: parseI18n(block),
      };
      const wrap = block.querySelector('[data-vh-bundle-products]');

      if (isNightstandBundle(block)) {
        const built = buildRow(block, mainProduct, { lockedToMain: true });
        if (built) {
          wrap.appendChild(built.row);
          block._vhBundle.rows.push(built.state);
        }
      }

      const recommendationLimit = isNightstandBundle(block) ? 2 : 3;
      products
        .filter((product) => Number(product.id) !== Number(mainProduct.id))
        .slice(0, recommendationLimit)
        .forEach((product) => {
          const built = buildRow(block, product);
          if (!built) return;
          wrap.appendChild(built.row);
          block._vhBundle.rows.push(built.state);
          setVariant(block, built.state, built.state.variant);
        });

      if (!block._vhBundle.rows.length) {
        const rule = block.querySelector('.vh-sofa-bundle__rule');
        if (rule) rule.textContent = saveText(block, '−6%', 2);
      }
      if (cart) updateCartState(block, cart);
      syncLockedRow(block);
      recalc(block);
    } catch (error) {
      console.error('[Valleans bundle]', error);
      block.hidden = true;
    } finally {
      block.dataset.vhBundleLoading = 'false';
    }
  };

  const init = (scope = document) => scope.querySelectorAll?.(ROOT).forEach(initBlock);

  document.addEventListener('variant:update', (event) => {
    const id = Number(event.detail?.resource?.id || 0);
    const productId = String(event.detail?.data?.productId || '');
    document.querySelectorAll(ROOT).forEach((block) => {
      if (!block._vhBundle) return;
      if (productId && String(block.dataset.productId) !== productId) return;
      if (id) {
        block._vhBundle.mainVariantId = id;
        block.dataset.selectedVariantId = String(id);
        syncLockedRow(block);
      }
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();

